import { type FC, type ReactNode, useMemo } from "react";
import { useInView } from "react-intersection-observer";

import { IconX } from "@btnet/blueprint-icons";
import { BTText, type TBTStatusBadgeDisplayTypes } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";
import { useWebviewContext } from "@btnet/utilities-webview";

import {
    BdsActionBar,
    BdsButtonGroup,
    BdsContentHeader,
    BdsRollupButton,
} from "@buildertrend/components";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { isInIframe } from "utilities/url/url";

import { BTItemHeader } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader";
import { type TBTItemHeaderIcon } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader.types";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import {
    EntityCopyLinkModalContent,
    EntityShareButtonBDS,
    type ISharingConfig,
} from "commonComponents/entity/sharing/EntityShareButton/EntityShareButton";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";
import { HotkeyCommands } from "commonComponents/utilities/Hotkey/hotkey.utility";

import "./BTModalLayoutBDS.less";

interface IBTModalLayoutBDSBaseProps {
    /**
     * A boolean to determine if the modal should use the BDS layout or the legacy layout.
     * @default false
     */
    useBDSLayout?: boolean;
    /**
     * The header for the modal layout component/ use this to override the default header.
     * @default undefined
     */
    header?: ReactNode;
    /**
     * The title of the modal
     */
    title?: string;
    /**
     * The subtitle of the modal
     */
    subtitle?: string;
    /**
     * The sharing configuration for the modal
     */
    sharing?: ISharingConfig;
    /**
     * The footer content for the modal
     */
    footerContent?: ReactNode;
    /**
     * The configuration for the modal
     */
    modalConfig?: IModalConfiguration;
    /**
     * The class name for the modal
     */
    className?: string;
    /**
     * Controls whether the close button and click-outside-to-close mask are enabled.
     * When in a webview context, both the close button and mask are always hidden regardless of this value.
     * @default true
     */
    closable?: boolean;
    /**
     * The children nodes
     */
    children: ReactNode;
    /**
     * A boolean to determine if the BDS layout content should have padding
     * @default true
     */
    useBDSLayoutContentPadding?: boolean;
    /**
     * The status message to be displayed in status badge of the header.
     * @default undefined
     */
    statusMessage?: string;
    /**
     * The test id for the status badge. This is required if you want to display a status badge.
     * @default undefined
     */
    statusTestId?: string;
    /**
     * The type of status badge to be displayed.
     * @default undefined
     */
    statusType?: TBTStatusBadgeDisplayTypes;
    additionalHeaderContent?: ReactNode;
    layoutRef?: React.RefObject<HTMLDivElement>;
    headerRef?: React.RefObject<HTMLDivElement>;
    /**
     * Optional array of icon action configurations for the header.
     */
    iconActions?: TBTItemHeaderIcon[];
}

/**
 * `useBTItemHeader` gates the new BTItemHeader rendering path. When true, `testId`
 * must be supplied so the new header has a stable test hook; when false/undefined the
 * legacy BdsContentHeader path is used and `testId` remains optional.
 */
type IBTModalLayoutHeaderVariant =
    | {
          useBTItemHeader: true;
          testId: string;
      }
    | {
          useBTItemHeader?: false;
          testId?: string;
      };

export type IBTModalLayoutBDSProps = IBTModalLayoutBDSBaseProps & IBTModalLayoutHeaderVariant;

/**
 * The modal layout component built using the BDS components.
 * This component is used to create a modal layout with a header, content, and footer.
 *
 * @param useBDSLayout - A boolean to determine if the modal should use the BDS layout or the legacy layout.
 * @param title - The title of the modal.
 * @param subtitle - The subtitle of the modal.
 * @param sharing - The sharing configuration for the modal.
 * @param footerContent - The footer content for the modal.
 * @param modalConfig - The modal configuration for the modal.
 * @param className - The class name for the modal.
 * @param closable - A boolean to determine if the modal is closable.
 * @param useBDSContentPadding - A boolean to determine if the BDS layout content should have padding.
 *
 */

export const BTModalLayoutBDS: FC<IBTModalLayoutBDSProps & ITrackingProp> = track({})((props) => {
    const {
        useBDSLayout = false,
        header,
        className,
        closable,
        title,
        subtitle,
        sharing,
        modalConfig,
        footerContent,
        children,
        tracking,
        useBDSLayoutContentPadding = true,
        statusMessage,
        statusTestId,
        statusType,
        additionalHeaderContent,
        layoutRef,
        headerRef,
        testId,
        iconActions,
        useBTItemHeader = false,
    } = props;
    const webviewContext = useWebviewContext();
    const cleanedTitle = title ? `-${title.replace(/[^a-zA-Z0-9]/g, "")}` : "";
    const { ref: topRef, inView: topInView } = useInView();
    const { ref: bottomRef, inView: bottomInView } = useInView();

    const defaultCloseable = closable ?? true;
    const modalMask = (
        <div
            className="BTModalMaskBDS"
            onClick={() => {
                modalConfig?.beforeClose();
                tracking?.trackEvent({
                    event: "ModalClose",
                    extraInfo: {
                        isFromClickOut: true,
                    },
                });
            }}
        />
    );

    let statusProps = {};

    if (statusMessage && statusTestId && statusType) {
        statusProps = {
            statusMessage,
            statusTestId,
            statusType,
        };
    }

    const defaultHeader = (
        <BdsContentHeader
            testid="modalHeader"
            jobName={subtitle ?? ""}
            title={title ?? ""}
            slotNoWrap
            innerRef={headerRef}
            slot={
                <BdsActionBar id={`modalActionBar${cleanedTitle}`} testid="modalActionBar" grow>
                    {defaultCloseable && (
                        <BdsButtonGroup
                            id={`modalHeaderActionBarButtonGroup${cleanedTitle}`}
                            testid="modalHeaderActionBarButtonGroup"
                            menuTestid="modalHeaderActionBarHeaderMenu"
                            rollupTestid="modalHeaderActionBarRollupMenu"
                        >
                            <EntityShareButtonBDS
                                id={`entityShareButtonBds${cleanedTitle}`}
                                sharing={sharing}
                            />
                            {additionalHeaderContent}
                            {!webviewContext && (
                                <BdsRollupButton
                                    id={`closeButton${cleanedTitle}`}
                                    testid="close"
                                    hotkey={HotkeyCommands.close.command}
                                    icon={<IconX />}
                                    ariaLabel="Close"
                                    displayType="tertiary"
                                    onClick={modalConfig?.beforeClose}
                                    value="close"
                                    tooltip="Close"
                                />
                            )}
                        </BdsButtonGroup>
                    )}
                </BdsActionBar>
            }
            {...statusProps}
        />
    );

    const beforeClose = modalConfig?.beforeClose;

    const modalIconActions = useMemo<TBTItemHeaderIcon[]>(() => {
        const actions: TBTItemHeaderIcon[] = [];

        if (sharing) {
            actions.push({
                icon: "share",
                popoverContent: <EntityCopyLinkModalContent sharing={sharing} />,
                inMenuMdBreakpoint: true,
                onClick: () => {},
            });
        }

        if (iconActions) {
            actions.push(...iconActions);
        }

        if (defaultCloseable && !webviewContext) {
            actions.push({
                icon: "closeX",
                onClick: () => beforeClose?.(),
                pinned: true,
            });
        }

        return actions;
    }, [beforeClose, defaultCloseable, iconActions, sharing, webviewContext]);

    const modalItemHeader = (
        <div ref={headerRef}>
            <BTItemHeader
                className="p-4 bt-modal-item-header"
                testid={`${testId ?? "bt"}-modal-item-header`}
                ariaLabel="header"
            >
                <div className="flex flex-row items-center">
                    <div className="flex flex-col">
                        {subtitle && (
                            <BTText size="distinct-sm" className="whitespace-nowrap">
                                {subtitle}
                            </BTText>
                        )}
                        {title && (
                            <BTText
                                data-testid="modalTitle"
                                size="distinct-lg"
                                className="whitespace-nowrap"
                            >
                                {title}
                            </BTText>
                        )}
                        {statusMessage && statusType && statusTestId && (
                            <BTItemHeader.Metadata
                                badgeText={statusMessage}
                                badgeDisplayType={statusType}
                                testid={statusTestId}
                            />
                        )}
                    </div>
                    <BTItemHeader.ActionButtons
                        testid="bt-item-header-action-buttons"
                        iconActions={modalIconActions}
                    />
                </div>
            </BTItemHeader>
        </div>
    );

    const getTitleOrHeader = () => {
        if (header) {
            return header;
        } else if (useBTItemHeader) {
            return modalItemHeader;
        } else if (title) {
            return defaultHeader;
        } else {
            return null;
        }
    };

    const headerContent = getTitleOrHeader();

    const model = (
        <>
            {useBDSLayout ? (
                <>
                    <div
                        data-testid={testId ?? "modalLayout"}
                        className={cn("bds bt-modal-layout-bds flex flex-col w-full", className, {
                            "bt-modal-layout-bds--header-shadow": !topInView,
                            "bt-modal-layout-bds--footer-shadow": !bottomInView,
                        })}
                    >
                        {headerContent && (
                            <div className="bt-modal-layout-bds-header">{headerContent}</div>
                        )}

                        <div
                            className={cn("bt-modal-layout-bds-content flex-1 overflow-y-auto", {
                                "bt-modal-layout-bds-content--no-padding":
                                    !useBDSLayoutContentPadding,
                            })}
                            data-testid="modalContent"
                            ref={layoutRef}
                        >
                            <div ref={topRef} />
                            {children}
                            <div ref={bottomRef} />
                        </div>

                        {footerContent && (
                            <div
                                data-testid="modalFooter"
                                className="flex flex-row bt-modal-layout-bds-footer items-center"
                            >
                                {footerContent}
                            </div>
                        )}
                    </div>
                    {closable !== false && !webviewContext && modalConfig?.beforeClose && modalMask}
                </>
            ) : (
                <div
                    className={cn("BTModalLayoutBDS", className, {
                        HeaderShadow: !topInView,
                    })}
                    data-testid={testId ?? "modalLayout"}
                    ref={layoutRef}
                >
                    {headerContent}
                    <div className="ModalContentContainer">
                        <div ref={topRef} />
                        {children}
                        <div ref={bottomRef} />
                    </div>
                    {footerContent && (
                        <div
                            className={cn("BTModalFooterBDS", {
                                FooterShadow: !bottomInView,
                            })}
                            data-testid="modalFooter"
                        >
                            {footerContent}
                        </div>
                    )}
                    {closable !== false && !webviewContext && modalConfig?.beforeClose && modalMask}
                </div>
            )}
        </>
    );
    return isInIframe() ? <FocusProvider>{model}</FocusProvider> : model;
});
